import { DEFAULT_CURRENCY_CODE, Inject, Injectable, LOCALE_ID } from '@angular/core';
import { ApiService } from '@quorum/api';
import { VehicleInspectionReportHeader, VehicleInspectionTask } from '@quorum/models/xs-resource';
import { renderInspectionReportPdf } from './inspection-report.painter';
import {
  BrakeMeasurementOption,
  buildInspectionReportViewModel,
  collectCategoryIds,
  RawInspectionPayload,
  RawQuoteLine,
  TaskSequence,
  TreadDepthThresholds,
} from './inspection-report.view-model';

// Comfortably above the largest inspection (every task across all types on a dealer is ~400).
const TASK_PAGE_SIZE = 500;

// What the inspection screen already holds that the report needs: the vcr-report header and
// quote lines, the rooftop's brake measurement options (the inspection only stores the chosen
// option's id) and tread-depth thresholds, and the tire tasks the Dual Rear Tires toggle is
// currently hiding.
export interface InspectionReportContext {
  readonly header: VehicleInspectionReportHeader | null;
  readonly quoteLines: readonly RawQuoteLine[];
  readonly brakeMeasurements: readonly BrakeMeasurementOption[];
  readonly treadDepthThresholds: TreadDepthThresholds | null;
  readonly omittedTaskIds: readonly number[];
}

@Injectable({ providedIn: 'root' })
export class InspectionReportPdfService {
  constructor(
    private readonly apiService: ApiService,
    @Inject(LOCALE_ID) private readonly locale: string,
    @Inject(DEFAULT_CURRENCY_CODE) private readonly currencyCode: string
  ) {}

  // Reads the inspection fresh from the server rather than the screen's copy, which the inspection
  // component reshapes (OnStar and windshield tasks are moved out of `categories`) before use.
  async buildReportPdf(workOrderId: number, context: InspectionReportContext): Promise<Blob> {
    const inspection: RawInspectionPayload | null = await this.apiService
      .get(`v/2/vip/inspections/${workOrderId}`)
      .toPromise();
    if (!inspection) {
      throw new Error(`No inspection found for work order ${workOrderId}`);
    }

    const taskSequences = await this.fetchTaskSequences(collectCategoryIds(inspection));

    return renderInspectionReportPdf(
      buildInspectionReportViewModel({
        inspection,
        taskSequences,
        header: context.header,
        brakeMeasurements: context.brakeMeasurements,
        treadDepthThresholds: context.treadDepthThresholds,
        omittedTaskIds: context.omittedTaskIds,
        quoteLines: context.quoteLines,
        currency: { locale: this.locale, currencyCode: this.currencyCode },
      })
    );
  }

  // The inspection payload orders tasks by id; the admin-configured order lives on the task itself.
  private async fetchTaskSequences(categoryIds: readonly number[]): Promise<TaskSequence[]> {
    if (categoryIds.length === 0) {
      return [];
    }

    const tasks: VehicleInspectionTask[] | null = await this.apiService
      .get('v/1/fixed-operations/vehicle-inspection-tasks', {
        params: { categoryId: categoryIds.join(','), page: '1', pageSize: String(TASK_PAGE_SIZE) },
      })
      .toPromise();

    return tasks ?? [];
  }
}
